import Mock from "mockjs";
import setupMock from "@/utils/setupMock";

type MockApp = {
  id: number;
  name: string;
  platform: string;
  icon: string;
};

type MockVersion = {
  id: number;
  appId: number;
  version: string;
  versionCode: number;
  platform: string;
  downloadUrl: string;
  size: string;
  updateLog: string;
  isForce: number;
  isCurrent: number;
  createTime: string;
};

const APPS: MockApp[] = [
  { id: 1, name: "Steam 盒子", platform: "PC", icon: "📦" },
  { id: 2, name: "激活助手", platform: "PC", icon: "⚡" },
  { id: 3, name: "库存管家", platform: "PC", icon: "📊" },
  { id: 4, name: "交易机器人", platform: "PC", icon: "🤖" },
  { id: 5, name: "商城客户端", platform: "iOS / Android", icon: "🛒" },
];

let seq = 100;
let versions: MockVersion[] = [];

const make = (
  appId: number,
  version: string,
  versionCode: number,
  platform: string,
  size: string,
  updateLog: string,
  isForce: number,
  isCurrent: number,
  createTime: string
): MockVersion => ({
  id: seq++,
  appId,
  version,
  versionCode,
  platform,
  downloadUrl: `https://download.example.com/app/${appId}/${version}/package.zip`,
  size,
  updateLog,
  isForce,
  isCurrent,
  createTime,
});

const seed = () => {
  if (versions.length) return;
  versions = [
    make(
      1,
      "2.4.1",
      241,
      "PC",
      "86.5MB",
      "修复已知问题，优化启动速度",
      0,
      1,
      "2026-09-18 10:00:00"
    ),
    make(
      1,
      "2.3.8",
      238,
      "PC",
      "85.2MB",
      "新增 CDK 导入，支持批量激活",
      1,
      0,
      "2026-09-02 10:00:00"
    ),
    make(
      1,
      "2.2.5",
      225,
      "PC",
      "82.0MB",
      "优化内存占用",
      0,
      0,
      "2026-08-10 10:00:00"
    ),

    make(
      2,
      "1.8.0",
      180,
      "PC",
      "45.3MB",
      "支持一键激活，新增日志查看",
      0,
      1,
      "2026-09-12 10:00:00"
    ),
    make(
      2,
      "1.7.2",
      172,
      "PC",
      "44.1MB",
      "修复激活码输入问题",
      1,
      0,
      "2026-08-20 10:00:00"
    ),
    make(
      2,
      "1.6.0",
      160,
      "PC",
      "43.0MB",
      "首个正式版本",
      0,
      0,
      "2026-07-15 10:00:00"
    ),

    make(
      3,
      "3.2.0",
      320,
      "PC",
      "102.8MB",
      "重构库存同步逻辑，更稳定",
      0,
      1,
      "2026-09-20 10:00:00"
    ),
    make(
      3,
      "3.1.4",
      314,
      "PC",
      "101.5MB",
      "修复价格刷新异常",
      0,
      0,
      "2026-09-01 10:00:00"
    ),
    make(
      3,
      "3.0.2",
      302,
      "PC",
      "99.0MB",
      "新增多账号支持",
      1,
      0,
      "2026-08-05 10:00:00"
    ),

    make(
      4,
      "2.1.3",
      213,
      "PC",
      "58.6MB",
      "优化挂单速度，降低延迟",
      0,
      1,
      "2026-09-15 10:00:00"
    ),
    make(
      4,
      "2.0.9",
      209,
      "PC",
      "57.4MB",
      "修复交易回调丢失",
      1,
      0,
      "2026-08-22 10:00:00"
    ),
    make(
      4,
      "1.9.6",
      196,
      "PC",
      "55.2MB",
      "新增风控策略",
      0,
      0,
      "2026-07-30 10:00:00"
    ),

    make(
      5,
      "1.5.2",
      152,
      "Android",
      "38.9MB",
      "适配 Android 15",
      0,
      1,
      "2026-09-10 10:00:00"
    ),
    make(
      5,
      "1.5.1",
      151,
      "iOS",
      "42.3MB",
      "修复深色模式问题",
      0,
      0,
      "2026-09-03 10:00:00"
    ),
    make(
      5,
      "1.4.0",
      140,
      "Android",
      "37.5MB",
      "新增推送通知",
      1,
      0,
      "2026-08-12 10:00:00"
    ),
  ];
};

const normalizeFlag = (value: any) =>
  value === true || value === 1 || value === "1" ? 1 : 0;

setupMock({
  setup: () => {
    Mock.mock(new RegExp("/app/list"), () => {
      seed();
      const data = APPS.map((app) => {
        const list = versions.filter((v) => v.appId === app.id);
        const current = list.find((v) => v.isCurrent === 1) || list[0];
        return {
          ...app,
          latestVersion: current?.version || "-",
          versionCount: list.length,
        };
      });
      return { code: 200, data, message: "ok" };
    });

    Mock.mock(new RegExp("/app/version/list"), (options) => {
      seed();
      const url = new URL(options.url, "http://mock.local");
      const appId = Number(url.searchParams.get("appId"));
      const list = versions
        .filter((v) => (appId ? v.appId === appId : true))
        .slice()
        .sort((a, b) => b.createTime.localeCompare(a.createTime));
      return { code: 200, data: { list, total: list.length }, message: "ok" };
    });

    Mock.mock(new RegExp("/app/version/add"), (options) => {
      seed();
      const body = JSON.parse(options.body || "{}");
      const appId = Number(body.appId);
      versions.unshift({
        id: seq++,
        appId,
        version: String(body.version || ""),
        versionCode: Number(body.versionCode || 0),
        platform: String(body.platform || "PC"),
        downloadUrl: String(body.downloadUrl || ""),
        size: String(body.size || ""),
        updateLog: String(body.updateLog || ""),
        isForce: normalizeFlag(body.isForce),
        isCurrent: 0,
        createTime: new Date()
          .toLocaleString("zh-CN", { hour12: false })
          .replace(/\//g, "-"),
      });
      return { code: 200, message: "新增版本成功" };
    });

    Mock.mock(new RegExp("/app/version/update"), (options) => {
      const body = JSON.parse(options.body || "{}");
      const target = versions.find((v) => v.id === Number(body.id));
      if (!target) {
        return { code: 500, message: "版本不存在" };
      }
      target.version = String(body.version ?? target.version);
      target.versionCode = Number(body.versionCode ?? target.versionCode);
      target.platform = String(body.platform ?? target.platform);
      target.downloadUrl = String(body.downloadUrl ?? target.downloadUrl);
      target.size = String(body.size ?? target.size);
      target.updateLog = String(body.updateLog ?? target.updateLog);
      target.isForce = normalizeFlag(body.isForce);
      return { code: 200, message: "更新成功" };
    });

    Mock.mock(new RegExp("/app/version/delete"), (options) => {
      const body = JSON.parse(options.body || "{}");
      const target = versions.find((v) => v.id === Number(body.id));
      if (!target) {
        return { code: 500, message: "版本不存在" };
      }
      if (target.isCurrent === 1) {
        return { code: 500, message: "当前线上版本不可删除" };
      }
      versions = versions.filter((v) => v.id !== Number(body.id));
      return { code: 200, message: "删除成功" };
    });

    Mock.mock(new RegExp("/app/version/publish"), (options) => {
      const body = JSON.parse(options.body || "{}");
      const target = versions.find((v) => v.id === Number(body.id));
      if (!target) {
        return { code: 500, message: "版本不存在" };
      }
      versions.forEach((v) => {
        if (v.appId === target.appId) {
          v.isCurrent = v.id === target.id ? 1 : 0;
        }
      });
      return { code: 200, message: "发布成功" };
    });
  },
});
